import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';

// Components
import Label from '../../components/Label';
import CustomButton from '../../components/CustomButton';
import FormikTextField from '../../components/form/FormikTextField';
import FormikInputNumber from '../../components/form/FormikInputNumber';
import FormikAutocomplete from '../../components/form/FormikAutocomplete';

// Prime-React Components
import { RadioButton } from 'primereact/radiobutton';
import { Sidebar } from 'primereact/sidebar';

// Redux-Actions
import { addHR, editHR } from '../../redux/actions/hr';
import { checkEmailMobile, editUserDetailsAction } from '../../redux/actions/admin';

// Redux-Slices
import { apiStatusClear } from '../../redux/slices/apiStatus';

// Constants
import { BUTTON_TYPES, LABEL_TYPES, ROLES } from '../../constants/common';

// Utils
import { onSuggestSkils } from '../../utils/pageUtils';

// Apis
import { updateData } from '../../redux/slices/hr';
import { addEditHRSchema } from '../../schema/hr.schema';

const AddEditHR = (props) => {
  const dispatch = useDispatch();
  const { onHide, show, ID } = props;
  const { userDetails } = useSelector((state) => state.adminDashboard);
  const { isSucceed, isLoading } = useSelector((state) => state.apiStatus);
  const [isUpdatedSuccess, setIsUpdatedSuccess] = useState(false);
  const [softSkilSuggestion, setSoftSkilSuggestion] = useState([]);
  const [hrData, setHrData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    gender: '',
    yearOfExperience: null,
    monthsOfExperience: null,
    techStack: []
  });

  const handleSuggestion = (event) => {
    setSoftSkilSuggestion(onSuggestSkils(event));
  };

  useEffect(() => {
    if (ID) {
      dispatch(editUserDetailsAction(ID));
    }
  }, [dispatch, ID]);

  useEffect(() => {
    if (ID && userDetails && Object.keys(userDetails).length !== 0) {
      setHrData({
        firstName: `${userDetails?.FirstName}`,
        lastName: `${userDetails?.LastName}`,
        email: `${userDetails?.Email}`,
        mobile: `${userDetails?.Mobile || ''}`,
        gender: parseInt(userDetails.Gender),
        yearOfExperience: userDetails?.YearsOfExperience,
        monthsOfExperience: userDetails?.MonthsOfExperience,
        techStack: userDetails?.TechStack || []
      });
    }
  }, [userDetails, ID]);

  useEffect(() => {
    if (show && isUpdatedSuccess && isSucceed) {
      onHide(false);
      document.body.classList.remove('sidebar-open');
      dispatch(updateData());
      dispatch(apiStatusClear());
      setHrData({
        firstName: '',
        lastName: '',
        email: '',
        mobile: '',
        gender: '',
        yearOfExperience: null,
        monthsOfExperience: null,
        techStack: []
      });
      formik.resetForm();
      document.body.classList.remove('sidebar-open');
      setIsUpdatedSuccess(false);
    }
  }, [isSucceed]);

  useEffect(() => () => setIsUpdatedSuccess(false), []);

  const formik = useFormik({
    initialValues: hrData,
    validationSchema: addEditHRSchema,
    onSubmit: (values) => handleSubmit(values),
    enableReinitialize: true
  });

  const handleSubmit = (values) => {
    const formData = {
      firstName: values.firstName?.trim(),
      lastName: values.lastName?.trim(),
      gender: values.gender,
      email: values.email?.trim(),
      mobile: values.mobile?.trim(),
      yearOfExperience: values.yearOfExperience,
      monthsOfExperience: values.monthsOfExperience,
      techStack: values.techStack,
      ...(userDetails?.UserTypeId === ROLES.HR_AND_INTERVIEWER &&
        userDetails?.UMap?.length && {
          skills: userDetails.UMap.map((skill) => skill.MapT.ID)
        }),
      isHR: true
    };
    if (!formData.mobile) {
      delete formData.mobile;
    }
    if (ID) {
      dispatch(
        editHR({
          formData,
          HrId: ID
        })
      );
    } else {
      dispatch(addHR(formData));
    }
    setIsUpdatedSuccess(true);
  };

  const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
  const getFormErrorMessage = (name) => {
    return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
  };

  return (
    <Sidebar
      ID={ID}
      visible={show}
      onHide={() => {
        onHide(true);
        document.body.classList.remove('sidebar-open');
      }}
      position="right"
      className="sidebar-drawer">
      <div className="form-box-wrapper">
        <div className="title-wrapper">
          <p className="card-title">{ID ? 'Edit' : 'Add'} HR</p>
          <button
            className="p-sidebar-close"
            onClick={() => {
              onHide(false);
              document.body.classList.remove('sidebar-open');
              setHrData({
                firstName: '',
                lastName: '',
                email: '',
                mobile: '',
                gender: '',
                yearOfExperience: null,
                monthsOfExperience: null,
                techStack: []
              });
              formik.resetForm();
            }}>
            <span className="p-sidebar-close-icon pi pi-times" />
          </button>
        </div>

        <form onSubmit={formik.handleSubmit} className="p-fluid" autoComplete="off">
          <div className="form-row-wrapper">
            <div className="form-col full-width">
              <div className="form-row-wrapper">
                <div className="form-col">
                  <div className="custom-form-group">
                    <FormikTextField
                      labelText={LABEL_TYPES.FIRST_NAME}
                      isMandatory
                      formik={formik}
                      name="firstName"
                      placeholder={LABEL_TYPES.FIRST_NAME + ' *'}
                    />
                  </div>
                </div>
                <div className="form-col">
                  <div className="custom-form-group">
                    <FormikTextField
                      labelText={LABEL_TYPES.LAST_NAME}
                      isMandatory
                      formik={formik}
                      name="lastName"
                      placeholder={LABEL_TYPES.LAST_NAME + ' *'}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="form-col full-width">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <FormikTextField
                    labelText={LABEL_TYPES.EMAIL}
                    isMandatory
                    formik={formik}
                    name="email"
                    placeholder={LABEL_TYPES.EMAIL + ' *'}
                    disabled={ID ? true : false}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      if (e.target.value && !formik.errors.email) {
                        dispatch(
                          checkEmailMobile({
                            email: e.target.value?.trim(),
                            ...(ID && { userId: ID })
                          })
                        );
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="form-col full-width">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <FormikTextField
                    labelText={LABEL_TYPES.MOBILE}
                    formik={formik}
                    name="mobile"
                    keyfilter="int"
                    placeholder={LABEL_TYPES.MOBILE}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      if (e.target.value && !formik.errors.mobile) {
                        dispatch(
                          checkEmailMobile({
                            mobile: e.target.value?.trim(),
                            ...(ID && { userId: ID })
                          })
                        );
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="form-col full-width">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="gender" text={LABEL_TYPES.GENDER} isBold isMandatory />
                  <div className="radio-list-wrapper">
                    <div className="form-group-outer-radiobutton">
                      <RadioButton
                        inputId="male"
                        value={1}
                        name="gender"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        checked={formik.values.gender === 1}
                      />
                      <Label htmlFor="male" text={LABEL_TYPES.MALE} isBold />
                    </div>
                    <div className="form-group-outer-radiobutton">
                      <RadioButton
                        inputId="female"
                        value={2}
                        name="gender"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        checked={formik.values.gender === 2}
                      />
                      <Label htmlFor="female" text={LABEL_TYPES.FEMALE} isBold />
                    </div>
                  </div>
                </div>
                {getFormErrorMessage('gender')}
              </div>
            </div>
            <div className="form-col full-width">
              <div className="form-row-wrapper">
                <div className="form-col">
                  <div className="custom-form-group ">
                    <FormikInputNumber
                      labelText={LABEL_TYPES.YEAR_EXPERIENCE}
                      isMandatory
                      formik={formik}
                      name="yearOfExperience"
                      placeholder="Years"
                      onValueChange={(e) => formik.setFieldValue('yearOfExperience', e.value)}
                      min={0}
                      max={100}
                    />
                  </div>
                </div>
                <div className="form-col">
                  <div className="custom-form-group">
                    <FormikInputNumber
                      labelText={LABEL_TYPES.MONTH_EXPERIENCE}
                      isMandatory
                      formik={formik}
                      name="monthsOfExperience"
                      placeholder="0-11"
                      onValueChange={(e) => formik.setFieldValue('monthsOfExperience', e.value)}
                      min={0}
                      max={11}
                    />
                  </div>
                </div>
              </div>
            </div>
            {userDetails?.UserTypeId !== ROLES.HR_AND_INTERVIEWER && (
              <div className="form-col full-width">
                <div className="form-group-outer">
                  <div className="custom-form-group autocomplete">
                    <FormikAutocomplete
                      labelText={LABEL_TYPES.SOFT_SKILLS}
                      formik={formik}
                      name="techStack"
                      placeholder={LABEL_TYPES.SOFT_SKILLS}
                      multiple
                      suggestions={softSkilSuggestion}
                      completeMethod={handleSuggestion}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="form-btn-wrapper">
            <CustomButton
              type="submit"
              variant="contained"
              className="ims-blue-btn"
              disabled={isLoading}>
              {BUTTON_TYPES.SAVE}
            </CustomButton>
            <CustomButton
              variant="contained"
              onClick={() => {
                onHide(false);
                document.body.classList.remove('sidebar-open');
                setHrData({
                  firstName: '',
                  lastName: '',
                  email: '',
                  mobile: '',
                  gender: '',
                  yearOfExperience: null,
                  monthsOfExperience: null,
                  techStack: []
                });
                formik.resetForm();
              }}
              color="error"
              className="gray-btn">
              {BUTTON_TYPES.CANCEL}
            </CustomButton>
          </div>
        </form>
      </div>
    </Sidebar>
  );
};

export default AddEditHR;
